/*
Enable import when plugins become available
import {
    tableComponent,
    listPagesComponent,
    fsLightboxComponent,
    lightGalleryComponent,
    swiperComponent,
    iconifyComponent,
    layoutSidebarButtons,
    canvasGridMode,
    youtubeAssetProvider,
    canvasFullSize,
    canvasEmptyState,
    flexComponent,
    accordionComponent,
    rteTinyMce,
} from '@grapesjs/studio-sdk-plugins'; */
import { googleFontsAssetProvider, layoutSidebarButtons } from '@grapesjs/studio-sdk-plugins';
import StudioEditor from '@grapesjs/studio-sdk/react';
import '@grapesjs/studio-sdk/style';
import { Container, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { useChangeRoute } from '../hooks/useChangeRoute';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Content } from '../store/models';
import { contentChanged, _activeContentIds, _contents } from '../store/slices/contentSlice';
import { _activeLanguageCode } from '../store/slices/editorSlice';
import { defaultGlobalStyles, defaultProject } from '../util/grapesjs';
import { updateLocationStyles, _selectedLocation, saveLocation } from '../store/slices/locationSlice';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		containerWidth: {
			width: '100vw',
			height: '100vh',
		},
		qr: {
			width: '100%',
			padding: 15,
		},
	})
);

const DashboardRedirect = () => {
	const toEditor = useChangeRoute('app-editor');
	useEffect(() => {
		toEditor();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return <></>;
};

export const Grape = () => {
	const contents = useAppSelector(_contents);
	const activeContentId = useAppSelector(_activeContentIds);
	const location = useAppSelector(_selectedLocation);
	const dispatch = useAppDispatch();
	const onContentChanged = bindActionCreators(contentChanged, dispatch);
	const updateLocation = bindActionCreators(updateLocationStyles, dispatch);
	const saveLocationStyles = bindActionCreators(saveLocation, dispatch);
	const activeLanguageCode = useAppSelector(_activeLanguageCode);

	const [selectedContent, setSelectedContent] = useState<Content>();
	const [projectId, setProjectId] = useState<string>();
	const [currentProject, setCurrentProject] = useState();
	const [currentStyles, setCurrentStyles] = useState<any>();

	useEffect(() => {
		if (Object.values(contents).length && !selectedContent) {
			if (activeContentId.length) {
				setSelectedContent(contents[activeContentId?.[0]]);
			} else {
				setSelectedContent(contents[activeContentId]);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contents, activeContentId]);

	useEffect(() => {
		if (selectedContent && !projectId) {
			const projectJson = selectedContent.projectJson?.find(
				(project: any) => project.language === activeLanguageCode
			);

			if (location?.grapesStyles) {
				setCurrentStyles(location.grapesStyles);
			} else {
				setCurrentStyles(defaultGlobalStyles);
			}
			setProjectId(projectJson?.uuid || uuidv4());

			if (!projectJson) {
				const languageContent = selectedContent?.languages?.find(
					(lang: any) => lang?.language === activeLanguageCode
				)?.content;
				setCurrentProject(languageContent || defaultProject);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedContent, activeLanguageCode]);

	const publishWebsite = async (editor: any, project: any) => {
		const files = await editor.runCommand('studio:projectFiles', { styles: 'inline' });
		const firstPage = files.find((file: any) => file.mimeType === 'text/html');

		if (selectedContent && selectedContent?.id) {
			if (!project?.uuid) {
				project.uuid = projectId;
				project.language = activeLanguageCode;
			}

			const newProjectStyles = project.dataSources?.[0]?.records;

			onContentChanged({
				language: activeLanguageCode,
				content: firstPage.content,
				id: selectedContent.id,
				projectJson: JSON.stringify(project),
				type: 'text',
			});
			updateLocation(newProjectStyles);
			saveLocationStyles();
		}
	};

	const loadFromSessionStorage = async () => {
		let project = null;
		if (selectedContent?.projectJson?.length === 1) {
			project = JSON.parse(selectedContent?.projectJson);
		} else {
			project = null;
		}
		return project;
	};

	const classes = useStyles();

	return (
		<Container className={classes.containerWidth} maxWidth="xl">
			{contents.length === 0 || !selectedContent || !projectId || !location || !currentStyles ? (
				<p>Loading...</p>
			) : (
				<StudioEditor
					options={{
						licenseKey: process.env.REACT_APP_GRAPE_JS_LICENSE,
						assets: {
							storageType: 'cloud',
						},
						pages: false,
						storage: {
							type: 'self',
							onLoad: async () => {
								const project = await loadFromSessionStorage();

								delete project?.dataSources?.[0]?.records;

								return {
									project: project || null,
								};
							},
							onSave: async ({ editor, project }) => {
								await publishWebsite(editor, project);
							},
						},
						plugins: [
							layoutSidebarButtons.init({
								sidebarButton({ buttonProps, createSidebarButton }) {
									return buttonProps;
								},
								sidebarButtons({ breakpoint, sidebarButtons, createSidebarButton }) {
									// Add a custom button for the default layout
									return !breakpoint
										? [
												...sidebarButtons,
												createSidebarButton({
													id: 'dashboard',
													icon:
														'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-house-icon lucide-house"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>',
													layoutComponent: {
														type: 'custom',
														component: () => <DashboardRedirect />,
													},
												}),
										  ]
										: sidebarButtons;
								},
							}),
							googleFontsAssetProvider,
						],
						identity: {
							id: location.id,
						},
						project: {
							type: 'web',
							id: projectId,
							default: {
								pages: [
									{
										name: 'Home',
										component: currentProject,
									},
								],
							},
						},
						globalStyles: {
							default: currentStyles,
						},
					}}
				/>
			)}
		</Container>
	);
};
